import { Button, Dropdown, Space } from "antd";
import { MoreOutlined } from "@ant-design/icons";
import { semantic } from "../../theme/tokens";

/**
 * Cấp độ hiển thị của một hành động trong hàng.
 * Khoá cứng ở đây để không màn nào tự chọn type/màu riêng nữa.
 */
const TONE_PROPS = {
    view: { type: "text" },
    edit: { type: "default" },
    default: { type: "default" },
    danger: { type: "default", danger: true },
    success: { type: "default", style: { color: semantic.success } },
    warning: { type: "default", style: { color: semantic.warning } },
};

const toneProps = (tone) => TONE_PROPS[tone] || TONE_PROPS.default;

/**
 * Bộ hành động trên một hàng của bảng danh sách.
 *
 * Quy ước trình bày (không cho từng màn tự quyết):
 *  - Số hành động <= `maxInline`: hiện hết dạng nút icon nhỏ.
 *  - Nhiều hơn: giữ (maxInline - 1) nút đầu, phần còn lại gộp vào menu "..." (kebab).
 *
 * Mọi nút đều `size="small"` + có `title` để hover ra nhãn.
 * Xác nhận (nếu cần) nằm trong handler của từng màn qua `confirmDelete`/`confirmAction`,
 * component này chỉ lo trình bày.
 *
 * @param {Array<{
 *   key: string,
 *   label: string,
 *   icon: import("react").ReactNode,
 *   onClick: function,
 *   tone?: "view"|"edit"|"default"|"danger"|"success"|"warning",
 *   hidden?: boolean,
 *   disabled?: boolean,
 *   loading?: boolean,
 * }>} actions
 * @param {number} maxInline
 */
export default function RowActions({ actions = [], maxInline = 3 }) {
    const visible = actions.filter((a) => a && !a.hidden);
    if (visible.length === 0) return null;

    const isOverflow = visible.length > maxInline;
    const inline = isOverflow ? visible.slice(0, maxInline - 1) : visible;
    const overflow = isOverflow ? visible.slice(maxInline - 1) : [];

    return (
        <Space size="small">
            {inline.map((a) => (
                <Button
                    key={a.key}
                    size="small"
                    icon={a.icon}
                    title={a.label}
                    onClick={a.onClick}
                    disabled={a.disabled}
                    loading={a.loading}
                    {...toneProps(a.tone)}
                />
            ))}

            {overflow.length > 0 && (
                <Dropdown
                    trigger={["click"]}
                    menu={{
                        items: overflow.map((a) => ({
                            key: a.key,
                            label: a.label,
                            icon: a.icon,
                            danger: a.tone === "danger",
                            // Menu item không có spinner: khi đang chạy thì chặn bấm lại
                            // để `loading` vẫn giữ được tác dụng chống double-submit.
                            disabled: a.disabled || a.loading,
                            onClick: () => a.onClick?.(),
                        })),
                    }}
                >
                    <Button size="small" type="text" icon={<MoreOutlined />} title="Thao tác khác" />
                </Dropdown>
            )}
        </Space>
    );
}

/**
 * Sinh định nghĩa cột "Thao tác" đã chuẩn hoá cho `columnDefs`.
 *
 * Chốt cứng: title "Thao tác", key "actions", align center, fixed right, width 120.
 * (ListTableCard đã bật scroll x nên fixed right luôn hoạt động.)
 *
 * @param {(record: object) => Array} getActions  Trả về mảng action theo từng hàng.
 * @param {object} opts
 * @param {number} opts.width
 * @param {number} opts.maxInline
 */
export function actionColumnDef(getActions, { width = 120, maxInline = 3 } = {}) {
    return {
        key: "actions",
        label: "Thao tác",
        fixed: true,
        column: {
            title: "Thao tác",
            key: "actions",
            width,
            align: "center",
            fixed: "right",
            render: (_, record) => <RowActions actions={getActions(record)} maxInline={maxInline} />,
        },
    };
}
